import { _lastOrUndefined } from '@naturalcycles/js-lib';
/**
 * QuizEngine is a service that provides all the logic needed to manage the quiz flow.
 *
 * @template BREAKPOINT A breakpoint is a situation in the quiz where the flow changes accordingly to a user action/answers.
 * @template BREAKPOINT_OPTION Enum that rapresent all the possible actions/answers for a certain breakpoint.
 * @template PAGE Enum that rapresent all the pages in the quiz.
 * @template QUIZ_DATA Interface that rapresent the data gathered in the quiz.
 */
export class QuizEngine {
  cfg;
  /**
   * @param cfg.breakpointActions - Object that contains a ChoiceFunction for each of the possible BREAKPOINT_OPTION that the user can take.
   * @param cfg.guardData - Object that contains a GuardFunction for each PAGE.
   */
  constructor(cfg) {
    this.cfg = cfg;
  }
  getNextPage(currentPage, flow) {
    for (const [sectionIndex, sectionPages] of flow.entries()) {
      const position = sectionPages.indexOf(currentPage);
      if (position === -1) {
        // Current page is not in current section
        continue;
      }
      if (position === sectionPages.length - 1) {
        if (sectionIndex === flow.length - 1) {
          // Current page is the last one in the flow, return undefined
          return undefined;
        }
        const nextSection = flow[sectionIndex + 1];
        if (!nextSection || nextSection.length < 1) {
          // Next section is empty, currentPage is the last one in the flow, return undefined
          return undefined;
        }
        // Next page is the first one in the next section
        return nextSection[0];
      }
      // Next page is the next one in this section
      return sectionPages[position + 1];
    }
    // Current page is not found in flow, just return current page
    return undefined;
  }
  getPrevPage(currentPage, flow) {
    for (const [sectionIndex, sectionPages] of flow.entries()) {
      const position = sectionPages.indexOf(currentPage);
      if (position === -1) {
        // Current page is not in current section
        continue;
      }
      if (position === 0) {
        if (sectionIndex === 0) {
          // Current page is the first one in the flow, return undefined
          return;
        }
        const prevSection = flow[sectionIndex - 1];
        if (!prevSection || prevSection.length < 1) {
          // Prev section is empty, current page is the first one in the flow, return undefined
          return;
        }
        // Previous page is the last one in the previous section
        return prevSection[prevSection.length - 1];
      }
      // Previous page is the previous in this section
      return sectionPages[position - 1];
    }
    // Current page is not found in flow, return undefined
    return;
  }
  getNextPageInFlow(from, flow) {
    const page = this.getNextPage(from, flow);
    console.log('[QuizEngine]: proceed to ' + page);
    return page;
  }
  getPreviousPageInFlow(from, flow) {
    const page = this.getPrevPage(from, flow);
    console.log('[QuizEngine]: back to ' + page);
    return page;
  }
  getSectionForPage(page, flow) {
    for (const [sectionIndex, sectionPages] of flow.entries()) {
      const position = sectionPages.find(p => p === page);
      if (!position) continue;
      return sectionIndex;
    }
    return;
  }
  getFirstPageInFlow(flow) {
    return flow?.[0]?.[0] || undefined;
  }
  getPreviousPagesInFlow(page, flow) {
    if (this.getSectionForPage(page, flow) === undefined) {
      return;
    }
    const firstPage = this.getFirstPageInFlow(flow);
    if (!firstPage) return;
    if (page === firstPage) {
      return [];
    }
    let previous = page;
    const previousPages = [];
    while (true) {
      previous = this.getPrevPage(previous, flow);
      if (!previous) {
        break;
      }
      previousPages.push(previous);
    }
    return previousPages.reverse();
  }
  getLastPageInFlow(flow) {
    const lastSection = _lastOrUndefined(flow.filter(section => section.length));
    return _lastOrUndefined(lastSection || []);
  }
  /**
   * This function processes the QuizFlow according to the answer/action take by the user at a certain breakpoint in the quiz.
   *
   * @param breakpoint - breakpoint we want to process
   * @param choice - choice we want to process
   * @param flow - quiz flow
   * @param data - quiz data
   * @returns a new quiz flow, updated according to the choice taken by the user
   */
  processBreakpointAction(breakpoint, choice, flow, data) {
    const updatedFlow = this.cfg.breakpointActions[breakpoint][choice](flow, data);
    console.log('[QuizEngine] Updated flow', updatedFlow);
    return updatedFlow;
  }
  getCompletedPercentage(flow, section, page) {
    const pageSection = this.getSectionForPage(page, flow);
    if (pageSection === undefined) {
      // page not found in section
      return;
    }
    if (section > pageSection) {
      //  section is further up in the flow
      return 0;
    }
    if (section < pageSection) {
      // page is in already finished section
      return 100;
    }
    const currentSection = flow[pageSection];
    const pagesInSection = Math.max(currentSection.length, this.cfg.expectedMinSectionLength || 0);
    const pagePositionInSection = currentSection.indexOf(page);
    if (pagePositionInSection === -1) return 0;
    return 100 / pagesInSection * (pagePositionInSection + 1);
  }
  /**
   *
   * @param page - page we want to process the route guard for
   * @param flow - quiz flow
   * @param data - quiz data
   * @returns
   * - undefined if all conditions are met for the user to access the current page
   * - the last page in the flow that user can access
   */
  processRouteGuardsForPage(page, flow, data) {
    // Build an array of current page + all previous pages in flow
    const previousPages = this.getPreviousPagesInFlow(page, flow);
    if (!previousPages) {
      return this.getFirstPageInFlow(flow);
    }
    const pages = [...previousPages, page];
    // Loop over the array and process all the GuardFunction(s)
    const result = pages.find(page => {
      if (this.cfg.guardData[page] === undefined) {
        return false;
      }
      const result = this.cfg.guardData[page](data);
      if (!result) {
        console.log(`[QuizEngine]: Route guard for page ${page} failed`);
      }
      return !result;
    });
    if (!result) {
      // All conditions are met
      return;
    }
    /*
     * Not all conditions are met.
     * Variable "result" contains the first page where the condition didn't meet.
     * We then get and return the page right before that page,
     * which is the last page in the flow that user can access.
     */
    const index = pages.indexOf(result);
    const redirectPage = pages[index - 1] || pages.find(x => x !== undefined);
    return redirectPage;
  }
  /**
   *
   * @param page - page we want to process the route guard for
   * @param flow - quiz flow
   * @param data - quiz data
   * @returns
   * - undefined if the user is allowed to access the current page
   * - previous page in the flow if the user is not allowed to access the current page
   * - first page in the flow if the page is not in the current flow
   */
  canActivateQuizPage(page, flow, data) {
    const pageSection = this.getSectionForPage(page, flow);
    if (pageSection === undefined) {
      // page is not in current flow, return the first page in the flow
      return this.getFirstPageInFlow(flow);
    }
    return this.processRouteGuardsForPage(page, flow, data);
  }
}
/**
Q: What is QuizFlow?
A: the QuizFlow rapresents the sequence of the quiz pages in the form array of arrays.
The first dimension of the array is used to rapresent the sections of the quiz.
 
Q: What is a breakpoint?
A: a breakpoint is a specific point in the quiz where the flow changes accordingly to a user action/answers.
 
Q: What is a BREAKPOINT_OPTION?
A: a BREAKPOINT_OPTION is an enum that rapresent all the possible actions/answers that the user can take on a certain breakpoint.
 
Q: What is a ChoiceFunction?
A: a ChoiceFunction is a function associated to a specific BREAKPOINT_OPTION.
It is run when the user makes the choice via processBreakpointAction.
It takes the current QuizFlow as input and returns a (updated) QuizFlow.
 
Q: What is a BreakpointAction?
A: a BreakpointAction is an object that contains a ChoiceFunction for each of the possible choices/actions that the user can take on a certain breakpoint.
 
Q: What is the BreakpointActions object?
A: The BreakpointActions object stores the breakpoint actions for each breakpoint in the quiz.
 
Q: What is a GuardFunction?
A: a GuardFunction defines the route guard of a quiz PAGE and it is similar to the Angular CanActivate guard.
It is associated to a PAGE, within the cfg.guardData object.
It takes QUIZ_DATA as input and returns true in case the rules are met, false otherwise.
 
Q: How does the route guards work?
A: cfg.guardData of type GuardQuizData defines a GuardFunction for each PAGE.
The object is built assuming that to be allowed on an hypotetical Page4,
The user needs to have in store the data gathered on Page1, Page2 and Page3.
 
Example:
const guardQuizData: GuardQuizData = {
  [Page.usps]: state => {
      return !!state?.name
  },
  [Page.birthday]: () => true,
  [Page.fertilityGoal]: state => {
    return !!state?.dateOfBirth
  },
  [Page.fertilityGoalInfo]: state => {
    return !!state?.fertilityGoal
  },
}
 
The code above reads as follow:
In order to be able to visit Page.fertilityGoalInfo, the user must have state.fertilityGoal set in the store,
as well as dateOfBirth and name as specified by the GuardFunctions of Page.fertilityGoal and Page.usps,
which are pages that precedes Page.fertilityGoalInfo in the quiz flow.
 */